/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-to-interactive-role, react/jsx-props-no-spreading, no-undef, func-names */
import React from 'react';
import cn from 'classnames';
import moment from 'moment';
import { Button } from 'antd';
// eslint-disable-next-line import/no-extraneous-dependencies
import { GiftOutlined } from '@ant-design/icons';
import { NOTIFICATION_ELEMENT_ID, CELLO_PRODUCT_ID } from '../../constants';
import { antdThemeToken } from '../../shared/styles/antd-theme';
import { t } from '../../utils/i18n';
import { openCelloWidget } from '../../utils';

const MESSAGES_APP_LINK_ID = 'messages-app-link';

const stopPropagation = (e) => {
  if (e.target?.offsetParent?.id === 'shorepay-button') {
    return false;
  }
  if (!(e.target?.id === MESSAGES_APP_LINK_ID)) {
    e.stopPropagation();
  }
  return true;
};

const messengerAppLinkStyle = {
  display: 'flex',
  color: antdThemeToken.colorText,
};

const formatAppointmentCount = (count) => (count > 99 ? '99+' : count);

const apmtStatusToHeadline = (apmtState) =>
  t(
    `notifications.appointment.states.${apmtState.replace(/_/g, '-')}.headline`
  );

const apmtStatusToClass = (apmtStatus) => {
  switch (apmtStatus) {
    case 'dispatched':
    case 'alternative_proposed':
    case 'waiting':
      return 'shore-icon-appointment-notification-info-01 is-warning';
    case 'rejected':
    case 'rejected_by_customer':
    case 'canceled':
    case 'expired':
      return 'shore-icon-backend-fail is-danger';
    case 'accepted':
    case 'competing':
    default:
      return 'shore-icon-backend-success is-success';
  }
};

function AppointmentNotification({
  id,
  services,
  timestamp,
  customerName,
  status,
  requestedDate,
  onClick,
  onClose,
}) {
  return (
    // Needed because of this bug https://github.com/evcohen/eslint-plugin-jsx-a11y/issues/295
    <li
      key={id}
      className="as-Notifications-appointmentItem"
      onClick={onClick}
      role="button"
    >
      <shore-icon
        role="button"
        name="delete"
        class="as-Notifications-appointmentClose"
        onClick={onClose}
      />
      <div
        className="as-Notifications-appointmentCreatedAt"
        title={moment(timestamp).format('llll')}
      >
        {moment(timestamp).format('ll')}
      </div>
      <div
        className={`as-Notifications-appointmentStatusIcon ${apmtStatusToClass(
          status
        )}`}
      />
      <div
        className="as-Notifications-appointmentHeadline"
        title={apmtStatusToHeadline(status)}
      >
        {apmtStatusToHeadline(status)}
      </div>
      <div className="as-Notifications-appointmentDetails">
        <div className="as-Notifications-appointmentRequestedDate">
          {moment(requestedDate).format('ddd, YYYY/MM/DD')}
        </div>
        <div>{services}</div>
        <div>{customerName}</div>
      </div>
    </li>
  );
}

function ClientMessage({ onClose, id }) {
  return (
    <div className="as-Notifications-clientMessage">
      <div className="as-Notifications-clientMessageBoundaryBlock" />
      <div>
        <h6>{t(`notifications.client-messages.${id}.title`)}</h6>
        <p>{t(`notifications.client-messages.${id}.body`)}</p>
      </div>
      <div className="as-Notifications-clientMessageBoundaryBlock">
        <button type="button" className="as-Modalclose" onClick={onClose}>
          <shore-icon name="delete" />
        </button>
      </div>
    </div>
  );
}

function Notifications({
  class: classValue,
  messageCount,
  appointmentCount,
  onAppointmentClick,
  onMessageClick,
  showAppointments,
  appointments: appointmentNotifications,
  onAppointmentNotificationClick,
  onAppointmentNotificationCloseClick,
  isLoaded,
  featureToggles,
  clientMessage,
  shouldShowProductNotification,
  onClientMessageCloseClick,
}) {
  const handleOnClickReferralBtn = () => openCelloWidget();

  return (
    <div className={`${classValue} as-Notifications`} onClick={stopPropagation}>
      {CELLO_PRODUCT_ID && (
        <div
          className="cello-launcher-announcement"
          style={{ position: 'relative' }}
        >
          <div className="as-Notifications-cello-button">
            <Button
              size="middle"
              type="default"
              variant="outlined"
              icon={<GiftOutlined />}
              onClick={handleOnClickReferralBtn}
              style={{ color: '#00D0BE', borderColor: '#00D0BE' }}
            >
              {t('button.referral.title')}
              <div className="cello-launcher" />
            </Button>
          </div>
          <div className="as-Notifications-cello-icon">
            <shore-icon
              name="handshake"
              class="as-Notifications-icon"
              onClick={handleOnClickReferralBtn}
              style={{ margin: '4px 10px' }}
            />
            <div className="cello-launcher" />
          </div>
        </div>
      )}
      {clientMessage && (
        <ClientMessage
          id={clientMessage.id}
          onClose={() => onClientMessageCloseClick(clientMessage.id)}
        />
      )}
      <shore-icon
        name="time"
        class="as-Notifications-icon"
        onClick={() => onAppointmentClick(!showAppointments)}
      >
        {appointmentCount > 0 && (
          <span className="as-Notifications-badge">
            {formatAppointmentCount(appointmentCount)}
          </span>
        )}
      </shore-icon>
      {featureToggles.messaging && (
        <a
          data-route
          href="#/messenger/conversations"
          style={messengerAppLinkStyle}
        >
          <shore-icon
            id={MESSAGES_APP_LINK_ID}
            name="comment"
            class="as-Notifications-icon"
            onClick={() => onMessageClick(messageCount)}
          >
            {messageCount > 0 && <span className="as-Notifications-badge" />}
          </shore-icon>
        </a>
      )}

      {shouldShowProductNotification && (
        <shore-icon
          name="bell"
          id={NOTIFICATION_ELEMENT_ID}
          class={cn('as-Notifications-icon', 'as-Notifications-iconbell')}
        />
      )}

      <div
        className={cn('as-Notifications-appointments', {
          'is-visible': showAppointments,
        })}
      >
        <header className="as-Notifications-appointmentsHeader">
          {t('notifications.menu-header.appointment')}
        </header>
        {!isLoaded ? (
          <shore-spinner class="as-Notifications-spinner" />
        ) : appointmentNotifications.length === 0 ? (
          <div className="as-Notifications-appointmentEmpty">
            {t('notifications.appointment.empty')}
          </div>
        ) : (
          <ul className="as-Notifications-appointmentList">
            {appointmentNotifications.map((notification) => (
              <AppointmentNotification
                {...notification}
                onClick={() =>
                  onAppointmentNotificationClick({
                    id: notification.id,
                    appointmentId: notification.appointmentId,
                    appointmentType: notification.appointmentType,
                    groupId: notification.groupId,
                  })
                }
                onClose={(e) => {
                  e.stopPropagation();
                  onAppointmentNotificationCloseClick(notification.id);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Notifications;
/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-to-interactive-role, react/jsx-props-no-spreading, no-undef, func-names */
